import { ChangeDetectionStrategy, Component, OnInit, inject, signal, computed, ElementRef, ViewChild, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { ListViewModule } from '@progress/kendo-angular-listview';
import { ThreadComponent } from './components/thread/thread.component';
import { BaseComponent } from 'src/app/core/common/base.component';
import { MessageService, Thread, Message } from './services/message-service';
import { SessionService } from 'src/app/core/services/session.service';
import { takeUntil, filter } from 'rxjs';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToasterService } from 'src/app/shared/notification-toast/toaster.service';
import { AppSettings } from 'src/assets/config/app-settings';
import { PhonePipe } from 'src/app/core/pipes/phone.pipe';
import { SseService } from 'src/app/core/services/sse.service';
import { TooltipDirective } from 'src/app/shared/directives/tooltip.directive';
import { generateAvatar, isLegacyAvatarUrl } from './avatar.util';

@Component({
  selector: 'app-messages',
  standalone: true,
  imports: [
    CommonModule,
    LayoutModule,
    ListViewModule,
    ThreadComponent,
    DialogsModule,
    ButtonsModule,
    InputsModule,
    FormsModule,
    PhonePipe,
    TooltipDirective
  ],
  templateUrl: './messages.page.html',
  styleUrls: ['./messages.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessagesComponent extends BaseComponent implements OnInit {
  public threads = signal<Thread[]>([]);
  private messageService = inject(MessageService);
  public session = inject(SessionService);
  private http = inject(HttpClient);
  private router = inject(Router);
  private toaster = inject(ToasterService);
  private settings = inject(AppSettings);
  private sse = inject(SseService);
  public count = signal(0);

  private pageSize = 10;
  private currentPage = 0;
  private totalRecords = 0;

  // Avatar URL cache to prevent recalculation
  private avatarCache = new Map<string, string>();
  private businessAvatarUrl: string = '';

  // Contact names cache (phone number -> custom name)
  private contactNamesCache = new Map<string, string>();

  // Chat interface
  public selectedThread = signal<Thread | null>(null);
  public messages = signal<Message[]>([]);
  public searchText = '';
  public currentMessage = '';
  @ViewChild('messagesContainer') messagesContainer?: ElementRef;

  // Compose dialog
  public showComposeDialog = signal(false);
  public composeRecipient = '';
  public composeMessage = '';
  public recipientError = signal('');
  public messageError = signal('');
  public isSending = signal(false);

  // Delete confirmation
  public showDeleteDialog = signal(false);
  public threadToDelete: Thread | null = null;

  // Selection mode
  public selectionMode = signal(false);
  public selectedThreads = signal<Set<string>>(new Set());

  // Archive view mode
  public viewMode = signal<'inbox' | 'archived' | 'deleted'>('inbox');

  // Template text feature
  public showTemplateMenu = signal(false);
  public templateTexts = [
    'Hello!',
    'Good morning!',
    'Good afternoon!',
    'Good evening!',
    'How are you?',
    'Thank you for contacting us!',
    'We appreciate your business!',
    'Have a great day!',
    'Looking forward to hearing from you!',
    'Thanks for reaching out!'
  ];

  // Emoji picker feature
  public showEmojiPicker = signal(false);
  public showComposeEmojiPicker = signal(false);
  public emojis = [
    '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
    '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙',
    '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔',
    '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥',
    '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮',
    '🤧', '🥵', '🥶', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐',
    '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉',
    '👆', '👇', '☝️', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '💔', '❣️', '💕',
    '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️',
    '🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '🥈', '🥉', '⭐', '🌟'
  ];

  // Attachment feature
  public attachedFiles = signal<File[]>([]);
  public composeAttachedFiles = signal<File[]>([]);

  // Pending optimistic message for new conversations
  private pendingOptimisticMessage: Message | null = null;

  // Contact edit mode
  public isEditingContact = signal(false);
  public editContactName = signal('');
  public editContactAvatar = signal('');
  public showAvatarPicker = signal(false);
  public rawPhoneNumber = '';

  constructor() {
    super();

    // Effect to load messages when thread is selected
    effect(() => {
      const thread = this.selectedThread();
      if (thread) {
        // Pass pending optimistic message if available
        this.loadMessages(thread.conversationId, this.pendingOptimisticMessage || undefined);
        // Clear the pending message after using it
        this.pendingOptimisticMessage = null;
      } else {
        this.messages.set([]);
      }
    }, { allowSignalWrites: true });
  }
  
  ngOnInit(): void {
    this.loadContactDataFromStorage();
    this.loadThreads(true);
    this.setupSseListener();
    this.setupPolling();
  }

  private loadContactDataFromStorage(): void {
    // Load avatars from localStorage
    const savedAvatars = localStorage.getItem('sms_contact_avatars');
    if (savedAvatars) {
      try {
        const avatarsObj = JSON.parse(savedAvatars);
        // Drop any entries left over from the retired external avatar host so
        // they get regenerated locally instead of showing blank.
        this.avatarCache = new Map(
          Object.entries(avatarsObj as Record<string, string>)
            .filter(([, url]) => !isLegacyAvatarUrl(url))
        );
      } catch (e) {
        console.error('Error loading avatars from storage:', e);
      }
    }

    // Load contact names from localStorage
    const savedNames = localStorage.getItem('sms_contact_names');
    if (savedNames) {
      try {
        const namesObj = JSON.parse(savedNames);
        this.contactNamesCache = new Map(Object.entries(namesObj));
      } catch (e) {
        console.error('Error loading contact names from storage:', e);
      }
    }
  }

  private saveContactDataToStorage(): void {
    // Save avatars to localStorage
    const avatarsObj = Object.fromEntries(this.avatarCache);
    localStorage.setItem('sms_contact_avatars', JSON.stringify(avatarsObj));

    // Save contact names to localStorage
    const namesObj = Object.fromEntries(this.contactNamesCache);
    localStorage.setItem('sms_contact_names', JSON.stringify(namesObj));
  }

  private setupPolling(): void {
    // Poll for new messages every 5 seconds as a fallback
    setInterval(() => {
      if (this.threads().length > 0 && this.viewMode() === 'inbox') {
        const number = this.session.user?.selectedNumber?.number!;
        const userId = this.session.user?.userId!;
        this.messageService.getThreads(number, userId, 0, 1, 'inbox') // Just get the most recent thread
          .pipe(takeUntil(this.destroyed$))
          .subscribe((response) => {
            if (response.value.length > 0) {
              const latestThread = response.value[0];
              const existingThread = this.threads().find(t => t.conversationId === latestThread.conversationId);

              // Check if this is a new message or updated thread
              if (!existingThread ||
                  new Date(latestThread.occurred_at).getTime() > new Date(existingThread.occurred_at).getTime()) {

                const myNumber = this.session.user?.selectedNumber?.number;
                const isFromMe = latestThread.from === myNumber;

                // Update threads list
                const updatedThreads = this.threads().filter(t => t.conversationId !== latestThread.conversationId);

                // Set unread count if not from me and not currently viewing
                if (!isFromMe && (!this.selectedThread() || this.selectedThread()?.conversationId !== latestThread.conversationId)) {
                  latestThread.unreadCount = 1;
                  this.toaster.info(`New message from ${latestThread.from}`);
                }

                this.threads.set([latestThread, ...updatedThreads]);

                // If viewing this conversation, reload messages
                if (this.selectedThread()?.conversationId === latestThread.conversationId) {
                  this.loadMessages(latestThread.conversationId);
                }
              }
            }
          });
      }
    }, 5000); // Poll every 5 seconds
  }

  private setupSseListener(): void {
    console.log('Setting up SSE listener for SMS messages');
    this.sse
      .getServerSentEvent()
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: (event) => {
          console.log('SSE event received:', event);

          if (event.topic !== 'sms.inbound') {
            return; // Ignore non-SMS events
          }

          console.log('Processing SMS inbound event:', event);
          const messageEvent = event.message;
          const myNumber = this.session.user?.selectedNumber?.number;

          // Determine the other party's number
          const otherNumber = messageEvent.from === myNumber ? messageEvent.to : messageEvent.from;

          // Create conversation ID (sorted alphabetically)
          const conversationId = messageEvent.from < messageEvent.to
            ? `${messageEvent.from}-${messageEvent.to}`
            : `${messageEvent.to}-${messageEvent.from}`;

          // Find if this thread already exists
          const existingThreadIndex = this.threads().findIndex(t => t.conversationId === conversationId);

          if (existingThreadIndex !== -1) {
            // Update existing thread
            const updatedThreads = [...this.threads()];
            const existingThread = updatedThreads[existingThreadIndex];

            // Update thread properties
            existingThread.text = messageEvent.text;
            existingThread.occurred_at = new Date(messageEvent.occurredAt);

            // Only increment unread if it's not from the current user and not the currently selected thread
            if (messageEvent.from !== myNumber &&
                (!this.selectedThread() || this.selectedThread()?.conversationId !== conversationId)) {
              existingThread.unreadCount = (existingThread.unreadCount || 0) + 1;
            }

            // Move to top of list
            updatedThreads.splice(existingThreadIndex, 1);
            updatedThreads.unshift(existingThread);

            this.threads.set(updatedThreads);
          } else {
            // Create new thread
            const newThread: Thread = {
              _id: conversationId,
              conversationId: conversationId,
              from: messageEvent.from,
              to: messageEvent.to,
              text: messageEvent.text,
              occurred_at: new Date(messageEvent.occurredAt),
              unreadCount: messageEvent.from !== myNumber ? 1 : 0
            };

            // Add to top of list
            this.threads.set([newThread, ...this.threads()]);
            this.count.set(this.count() + 1);
          }

          // If this message is for the currently open conversation, add or update message
          if (this.selectedThread()?.conversationId === conversationId) {
            const newMessage: Message = {
              _id: 'sse-' + Date.now(),
              from: messageEvent.from,
              to: messageEvent.to,
              text: messageEvent.text,
              media: messageEvent.media || [],
              occurred_at: new Date(messageEvent.occurredAt)
            };

            // Check if we have an optimistic message to replace (from same user sent in last 10 seconds)
            const currentMessages = this.messages();
            const now = Date.now();
            const recentOptimisticIndex = currentMessages.findIndex(m =>
              m._id.startsWith('temp-') &&
              m.from === newMessage.from &&
              m.to === newMessage.to &&
              (now - new Date(m.occurred_at).getTime() < 10000) // Within 10 seconds
            );

            if (recentOptimisticIndex !== -1) {
              // Replace optimistic message with real one
              const updatedMessages = [...currentMessages];
              updatedMessages[recentOptimisticIndex] = newMessage;
              this.messages.set(updatedMessages);
            } else {
              // Add new message if no optimistic message found
              this.messages.set([...currentMessages, newMessage]);
            }

            setTimeout(() => this.scrollToBottom(), 100);

            // Mark as read since it's the active conversation
            this.markThreadAsRead(conversationId);
          }

          // Show toast notification if not the active conversation
          if (messageEvent.from !== myNumber &&
              (!this.selectedThread() || this.selectedThread()?.conversationId !== conversationId)) {
            this.toaster.info(`New message from ${messageEvent.from}`);
          }
        },
        error: (error) => {
          console.error('SSE error:', error);
        },
        complete: () => {
          console.log('SSE connection closed');
        }
      });
  }

  private markThreadAsRead(conversationId: string): void {
    const threads = this.threads();
    const threadIndex = threads.findIndex(t => t.conversationId === conversationId);
    if (threadIndex !== -1 && threads[threadIndex].unreadCount) {
      const updatedThreads = [...threads];
      updatedThreads[threadIndex] = { ...updatedThreads[threadIndex], unreadCount: 0 };
      this.threads.set(updatedThreads);
    }
  }

  public loadThreads(initialLoad: boolean = false) {
    if (!initialLoad && (this.currentPage + 1) * this.pageSize >= this.totalRecords) {
      return; // No more records to load
    }

    if (!initialLoad) {
      this.currentPage++;
    }

    const number = this.session.user?.selectedNumber?.number;
    const userId = this.session.user?.userId;
    const skip = this.currentPage * this.pageSize;

    if (!number || !userId) {
      console.error('Missing required parameters:', { number, userId });
      return;
    }

    const currentView = this.viewMode();
    this.messageService.getThreads(number, userId, skip, this.pageSize, currentView)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response) => {
        this.count.set(response['@odata.count']);
        this.totalRecords = response['@odata.count'];

        const newThreads = response.value;
        this.threads.set(initialLoad ? newThreads : [...this.threads(), ...newThreads]);

        // Preload avatar images for smoother rendering
        this.preloadAvatars(newThreads);
      });
  }

  public openComposeDialog(): void {
    this.showComposeDialog.set(true);
    this.composeRecipient = '';
    this.composeMessage = '';
    this.recipientError.set('');
    this.messageError.set('');
  }

  public closeComposeDialog(): void {
    this.showComposeDialog.set(false);
    this.composeAttachedFiles.set([]);
  }

  private validatePhoneNumber(phone: string): boolean {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');

    // Check if it's a valid length (10 digits for US or 11 with country code)
    if (cleaned.length === 10 || cleaned.length === 11) {
      return true;
    }

    // Also accept E.164 format with +
    const e164Regex = /^\+?[1-9]\d{1,14}$/;
    return e164Regex.test(phone);
  }

  public sendNewMessage(): void {
    // Reset errors
    this.recipientError.set('');
    this.messageError.set('');

    // Validate recipient
    if (!this.composeRecipient.trim()) {
      this.recipientError.set('Please enter a phone number');
      return;
    }

    if (!this.validatePhoneNumber(this.composeRecipient)) {
      this.recipientError.set('Please enter a valid phone number');
      return;
    }

    // Validate message - must have either text or attachments
    if (!this.composeMessage.trim() && this.composeAttachedFiles().length === 0) {
      this.messageError.set('Please enter a message or attach a file');
      return;
    }

    // Send message
    this.isSending.set(true);

    // Clean and format phone number to E.164 format
    const cleanedTo = this.composeRecipient.replace(/\D/g, '');
    const formattedTo = cleanedTo.startsWith('1') ? `+${cleanedTo}` : `+1${cleanedTo}`;

    // Create FormData for file attachments
    const formData = new FormData();
    formData.append('to', formattedTo);
    formData.append('from', this.session.user?.selectedNumber?.number || '');
    if (this.composeMessage.trim()) {
      formData.append('text', this.composeMessage.trim());
    }
    formData.append('userId', this.session.user?.userId || '');

    // Append files if any
    const hasAttachments = this.composeAttachedFiles().length > 0;
    this.composeAttachedFiles().forEach((file) => {
      formData.append(`media`, file, file.name);
    });

    this.http.post(`${this.settings.apiBaseUrl}/api/sms/send`, formData)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.isSending.set(false);
          this.toaster.success(hasAttachments ? 'Message with attachments sent successfully' : 'Message sent successfully');

          const from = this.session.user?.selectedNumber?.number || '';
          const messageText = this.composeMessage.trim();

          // Create conversation ID using full E.164 format (same as backend)
          // Backend compares strings, so use the actual phone number values
          const conversationId = from < formattedTo
            ? `${from}-${formattedTo}`
            : `${formattedTo}-${from}`;

          // Check if thread already exists
          let existingThread = this.threads().find(t => t.conversationId === conversationId);

          if (existingThread) {
            // Update existing thread with new message
            const updatedThreads = this.threads().map(t => {
              if (t.conversationId === conversationId) {
                return {
                  ...t,
                  text: messageText,
                  occurred_at: new Date()
                };
              }
              return t;
            });
            this.threads.set(updatedThreads);
            existingThread = updatedThreads.find(t => t.conversationId === conversationId)!;
          } else {
            // Create new thread
            const newThread: Thread = {
              _id: Date.now().toString(),
              conversationId: conversationId,
              from: from,
              to: formattedTo,
              text: messageText,
              occurred_at: new Date(),
              unreadCount: 0,
              isRead: true
            };

            // Add to beginning of threads list
            this.threads.set([newThread, ...this.threads()]);
            this.totalRecords++;
            existingThread = newThread;
          }

          // Create optimistic message to show immediately
          this.pendingOptimisticMessage = {
            _id: 'temp-' + Date.now(),
            from: from,
            to: formattedTo,
            text: messageText,
            occurred_at: new Date(),
            media: hasAttachments ? this.composeAttachedFiles().map(f => ({
              url: URL.createObjectURL(f),
              content_type: f.type,
              size: f.size
            })) : undefined
          };

          // Clear compose form
          this.composeMessage = '';
          this.composeAttachedFiles.set([]);

          // Close dialog and select the thread
          // The effect will pick up the pendingOptimisticMessage
          this.closeComposeDialog();
          this.selectThread(existingThread);
        },
        error: (error) => {
          this.isSending.set(false);
          this.toaster.error(error?.error?.error || 'Failed to send message. Please try again.');
          console.error('Error sending message:', error);
        }
      });
  }

  public deleteThread(thread: Thread): void {
    this.threadToDelete = thread;
    this.showDeleteDialog.set(true);
  }

  public confirmDelete(): void {
    if (!this.threadToDelete) return;

    const conversationId = this.threadToDelete.conversationId;

    // Call API to delete conversation
    this.http.delete(`${this.settings.apiBaseUrl}/api/sms/conversation/${conversationId}`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          // Remove thread from local array
          const updatedThreads = this.threads().filter(t => t.conversationId !== conversationId);
          this.threads.set(updatedThreads);
          this.totalRecords--;

          this.showDeleteDialog.set(false);
          this.threadToDelete = null;
          this.toaster.success('Conversation deleted successfully');
        },
        error: (error) => {
          this.toaster.error('Failed to delete conversation. Please try again.');
          console.error('Error deleting conversation:', error);
        }
      });
  }

  public cancelDelete(): void {
    this.showDeleteDialog.set(false);
    this.threadToDelete = null;
  }

  // Chat interface methods
  public selectThread(thread: Thread): void {
    this.selectedThread.set(thread);
    // Mark thread as read when selected
    this.markThreadAsRead(thread.conversationId);
  }

  private loadMessages(conversationId: string, optimisticMessage?: Message): void {
    this.messageService.getMessages(conversationId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response) => {
        // If we have an optimistic message, add it to the end
        const messages = optimisticMessage
          ? [...response.value, optimisticMessage]
          : response.value;
        this.messages.set(messages);
        setTimeout(() => this.scrollToBottom(), 100);
      });
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      const element = this.messagesContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    }
  }

  public isMyMessage(message: Message): boolean {
    return message.from === this.session.user?.selectedNumber?.number;
  }

  public getMessageTime(date: Date): string {
    const messageDate = new Date(date);
    return messageDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  public getMessageDate(date: Date): string {
    const messageDate = new Date(date);
    const today = new Date();

    if (messageDate.toDateString() === today.toDateString()) {
      return 'Today';
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }

    return messageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  public getAvatarUrl(thread: Thread): string {
    const number = thread.from !== this.session.user?.selectedNumber?.number ? thread.from : thread.to;

    // Check cache first
    if (this.avatarCache.has(number)) {
      return this.avatarCache.get(number)!;
    }

    // Generate a self-contained avatar (initials use the contact's name when set).
    const url = generateAvatar(number, this.getContactName(thread));
    this.avatarCache.set(number, url);
    return url;
  }

  private preloadAvatars(threads: Thread[]): void {
    // Warm the avatar cache so each thread renders instantly. Avatars are inline
    // SVG data URIs now, so there's nothing to fetch over the network.
    threads.forEach(thread => this.getAvatarUrl(thread));
  }

  public getBusinessAvatarUrl(): string {
    // Cache business avatar URL on first call
    if (this.businessAvatarUrl) {
      return this.businessAvatarUrl;
    }

    const myNumber = this.session.user?.selectedNumber?.number || 'business';
    const label = this.session.user?.selectedNumber?.name || myNumber;
    this.businessAvatarUrl = generateAvatar(myNumber, label);
    return this.businessAvatarUrl;
  }

  public getContactName(thread: Thread): string {
    const phoneNumber = thread.from !== this.session.user?.selectedNumber?.number ? thread.from : thread.to;

    // Check if there's a custom name saved for this contact
    const customName = this.contactNamesCache.get(phoneNumber);
    return customName || phoneNumber;
  }

  public isPhoneNumber(value: string): boolean {
    // Check if the value consists only of phone number characters (digits, +, spaces, hyphens, parentheses)
    return /^[+\d\s()-]+$/.test(value);
  }

  public getPhoneNumber(thread: Thread): string {
    // Always returns the actual phone number, never the custom name
    return thread.from !== this.session.user?.selectedNumber?.number ? thread.from : thread.to;
  }

  public getTimeLabel(date: Date): string {
    const now = new Date();
    const messageDate = new Date(date);
    const diffMs = now.getTime() - messageDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;
    if (diffHours === 1) return '1h ago';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return messageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  public sendMessage(): void {
    // Must have either a message or attachments, and a selected thread
    if ((!this.currentMessage.trim() && this.attachedFiles().length === 0) || !this.selectedThread()) return;

    const messageText = this.currentMessage.trim();
    const phoneNumber = this.getPhoneNumber(this.selectedThread()!);

    // Create FormData for file attachments
    const formData = new FormData();
    formData.append('to', phoneNumber);
    formData.append('from', this.session.user?.selectedNumber?.number || '');
    if (messageText) {
      formData.append('text', messageText);
    }
    formData.append('userId', this.session.user?.userId || '');

    // Append files if any
    this.attachedFiles().forEach((file, index) => {
      formData.append(`media`, file, file.name);
    });

    // Optimistically add message to UI immediately
    const optimisticMessage: Message = {
      _id: 'temp-' + Date.now(),
      from: this.session.user?.selectedNumber?.number || '',
      to: phoneNumber,
      text: messageText,
      occurred_at: new Date(),
      media: this.attachedFiles().length > 0 ? this.attachedFiles().map(f => ({
        url: URL.createObjectURL(f),
        content_type: f.type,
        size: f.size
      })) : undefined
    };

    this.messages.set([...this.messages(), optimisticMessage]);
    this.currentMessage = '';

    // Clear attached files from UI
    const hasAttachments = this.attachedFiles().length > 0;
    this.attachedFiles.set([]);

    setTimeout(() => this.scrollToBottom(), 50);

    this.http.post<{ success: boolean, messageId: string }>(`${this.settings.apiBaseUrl}/api/sms/send`, formData)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          // Don't reload messages immediately - keep the optimistic message with blob URLs
          // The real message will come through SSE event or next message load
          // This prevents the attachment from disappearing while waiting for webhook
          this.toaster.success(hasAttachments ? 'Message with attachments sent' : 'Message sent');
        },
        error: (error) => {
          // Remove optimistic message on error
          this.messages.set(this.messages().filter(m => m._id !== optimisticMessage._id));
          this.toaster.error(error?.error?.error || 'Failed to send message');
          console.error('Error sending message:', error);
        }
      });
  }

  // Selection mode methods
  public toggleSelectionMode(): void {
    const newMode = !this.selectionMode();
    this.selectionMode.set(newMode);
    if (!newMode) {
      // Clear selections when exiting selection mode
      this.selectedThreads.set(new Set());
    }
  }

  public toggleThreadSelection(threadId: string): void {
    const selected = new Set(this.selectedThreads());
    if (selected.has(threadId)) {
      selected.delete(threadId);
    } else {
      selected.add(threadId);
    }
    this.selectedThreads.set(selected);
  }

  public isThreadSelected(threadId: string): boolean {
    return this.selectedThreads().has(threadId);
  }

  public selectAllThreads(): void {
    const allIds = new Set(this.threads().map(t => t._id));
    this.selectedThreads.set(allIds);
  }

  public unselectAll(): void {
    this.selectedThreads.set(new Set());
  }

  public deleteSelectedThreads(): void {
    const selectedIds = Array.from(this.selectedThreads());
    if (selectedIds.length === 0) return;

    const userId = this.session.user?.userId!;
    const customerId = this.session.user?.customer?._id!;

    // Get conversationIds from the selected thread IDs
    const conversationIds = this.threads()
      .filter(t => selectedIds.includes(t._id))
      .map(t => t.conversationId);

    // Call backend API to persist delete state
    this.messageService.deleteThreads(conversationIds, userId, customerId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          // Remove deleted threads from the list immediately
          const updatedThreads = this.threads().filter(t => !selectedIds.includes(t._id));
          this.threads.set(updatedThreads);

          // Exit selection mode
          this.selectionMode.set(false);
          this.selectedThreads.set(new Set());

          this.toaster.success(`${selectedIds.length} conversation${selectedIds.length > 1 ? 's' : ''} deleted`);
        },
        error: (error) => {
          console.error('Error deleting threads:', error);
          this.toaster.error('Failed to delete conversations');
        }
      });
  }

  public archiveSelectedThreads(): void {
    const selectedIds = Array.from(this.selectedThreads());
    if (selectedIds.length === 0) return;

    const userId = this.session.user?.userId!;
    const customerId = this.session.user?.customer?._id!;

    // Get conversationIds from the selected thread IDs
    const conversationIds = this.threads()
      .filter(t => selectedIds.includes(t._id))
      .map(t => t.conversationId);

    // Call backend API to persist archive state
    this.messageService.archiveThreads(conversationIds, userId, customerId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          // Remove archived threads from the list immediately (if in inbox view)
          if (this.viewMode() === 'inbox') {
            const updatedThreads = this.threads().filter(t => !selectedIds.includes(t._id));
            this.threads.set(updatedThreads);
          } else {
            // If in archived view, just mark as archived
            const updatedThreads = this.threads().map(t =>
              selectedIds.includes(t._id) ? { ...t, archived: true } : t
            );
            this.threads.set(updatedThreads);
          }

          // Exit selection mode
          this.selectionMode.set(false);
          this.selectedThreads.set(new Set());

          this.toaster.success(`${selectedIds.length} conversation${selectedIds.length > 1 ? 's' : ''} archived`);
        },
        error: (error) => {
          console.error('Error archiving threads:', error);
          this.toaster.error('Failed to archive conversations');
        }
      });
  }

  public unarchiveSelectedThreads(): void {
    const selectedIds = Array.from(this.selectedThreads());
    if (selectedIds.length === 0) return;

    const userId = this.session.user?.userId!;
    const customerId = this.session.user?.customer?._id!;

    // Get conversationIds from the selected thread IDs
    const conversationIds = this.threads()
      .filter(t => selectedIds.includes(t._id))
      .map(t => t.conversationId);

    // Call backend API to persist unarchive state
    this.messageService.unarchiveThreads(conversationIds, userId, customerId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          // Remove unarchived threads from the list immediately (if in archived view)
          if (this.viewMode() === 'archived') {
            const updatedThreads = this.threads().filter(t => !selectedIds.includes(t._id));
            this.threads.set(updatedThreads);
          } else {
            // If in inbox view, just mark as unarchived
            const updatedThreads = this.threads().map(t =>
              selectedIds.includes(t._id) ? { ...t, archived: false } : t
            );
            this.threads.set(updatedThreads);
          }

          // Exit selection mode
          this.selectionMode.set(false);
          this.selectedThreads.set(new Set());

          this.toaster.success(`${selectedIds.length} conversation${selectedIds.length > 1 ? 's' : ''} restored to inbox`);
        },
        error: (error) => {
          console.error('Error unarchiving threads:', error);
          this.toaster.error('Failed to restore conversations');
        }
      });
  }

  public restoreSelectedThreads(): void {
    // Restore from deleted back to inbox (undelete)
    const selectedIds = Array.from(this.selectedThreads());
    if (selectedIds.length === 0) return;

    const userId = this.session.user?.userId!;
    const customerId = this.session.user?.customer?._id!;

    // Get conversationIds from the selected thread IDs
    const conversationIds = this.threads()
      .filter(t => selectedIds.includes(t._id))
      .map(t => t.conversationId);

    // Call backend API to unarchive (which also undeletes)
    this.messageService.unarchiveThreads(conversationIds, userId, customerId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          // Remove restored threads from the deleted list
          const updatedThreads = this.threads().filter(t => !selectedIds.includes(t._id));
          this.threads.set(updatedThreads);

          // Exit selection mode
          this.selectionMode.set(false);
          this.selectedThreads.set(new Set());

          this.toaster.success(`${selectedIds.length} conversation${selectedIds.length > 1 ? 's' : ''} restored to inbox`);
        },
        error: (error) => {
          console.error('Error restoring threads:', error);
          this.toaster.error('Failed to restore conversations');
        }
      });
  }

  public permanentlyDeleteSelectedThreads(): void {
    // This could be implemented to actually delete from database
    // For now, it just removes from the deleted view
    const selectedIds = Array.from(this.selectedThreads());
    if (selectedIds.length === 0) return;

    // Remove from UI immediately
    const updatedThreads = this.threads().filter(t => !selectedIds.includes(t._id));
    this.threads.set(updatedThreads);

    // Exit selection mode
    this.selectionMode.set(false);
    this.selectedThreads.set(new Set());

    this.toaster.success(`${selectedIds.length} conversation${selectedIds.length > 1 ? 's' : ''} permanently deleted`);
  }

  public toggleArchiveView(): void {
    // Cycle through: inbox -> archived -> deleted -> inbox
    const currentMode = this.viewMode();
    let newMode: 'inbox' | 'archived' | 'deleted';

    if (currentMode === 'inbox') {
      newMode = 'archived';
    } else if (currentMode === 'archived') {
      newMode = 'deleted';
    } else {
      newMode = 'inbox';
    }

    this.viewMode.set(newMode);
    // Reset pagination and reload threads for the new view
    this.currentPage = 0;
    this.loadThreads(true);
    // Clear selected thread if switching views
    this.selectedThread.set(null);
  }

  public getViewTitle(): string {
    const mode = this.viewMode();
    if (mode === 'inbox') return 'Inbox';
    if (mode === 'archived') return 'Archived';
    return 'Deleted';
  }

  public getViewIcon(): string {
    const mode = this.viewMode();
    if (mode === 'inbox') return 'fa-inbox';
    if (mode === 'archived') return 'fa-archive';
    return 'fa-trash';
  }

  public getNextViewTooltip(): string {
    const mode = this.viewMode();
    if (mode === 'inbox') return 'View archived';
    if (mode === 'archived') return 'View deleted';
    return 'View inbox';
  }

  // Template text methods
  public toggleTemplateMenu(): void {
    this.showTemplateMenu.set(!this.showTemplateMenu());
  }

  public selectTemplate(template: string): void {
    this.currentMessage = template;
    this.showTemplateMenu.set(false);
  }

  public selectComposeTemplate(template: string): void {
    this.composeMessage = template;
    this.showTemplateMenu.set(false);
  }

  // Emoji picker methods
  public toggleEmojiPicker(): void {
    this.showEmojiPicker.set(!this.showEmojiPicker());
    if (this.showEmojiPicker()) {
      this.showTemplateMenu.set(false);
    }
  }

  public toggleComposeEmojiPicker(): void {
    this.showComposeEmojiPicker.set(!this.showComposeEmojiPicker());
    if (this.showComposeEmojiPicker()) {
      this.showTemplateMenu.set(false);
    }
  }

  public selectEmoji(emoji: string): void {
    this.currentMessage += emoji;
    this.showEmojiPicker.set(false);
  }

  public selectComposeEmoji(emoji: string): void {
    this.composeMessage += emoji;
    this.showComposeEmojiPicker.set(false);
  }

  // Attachment methods
  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const newFiles = Array.from(input.files);
      this.attachedFiles.set([...this.attachedFiles(), ...newFiles]);
      this.toaster.info(`${newFiles.length} file(s) attached`);
    }
    // Reset input so the same file can be selected again
    input.value = '';
  }

  public onComposeFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const newFiles = Array.from(input.files);
      this.composeAttachedFiles.set([...this.composeAttachedFiles(), ...newFiles]);
      this.toaster.info(`${newFiles.length} file(s) attached`);
    }
    // Reset input so the same file can be selected again
    input.value = '';
  }

  public removeAttachment(index: number): void {
    const files = [...this.attachedFiles()];
    files.splice(index, 1);
    this.attachedFiles.set(files);
  }

  public removeComposeAttachment(index: number): void {
    const files = [...this.composeAttachedFiles()];
    files.splice(index, 1);
    this.composeAttachedFiles.set(files);
  }

  public triggerFileInput(): void {
    const fileInput = document.getElementById('chat-file-input') as HTMLInputElement;
    fileInput?.click();
  }

  public triggerComposeFileInput(): void {
    const fileInput = document.getElementById('compose-file-input') as HTMLInputElement;
    fileInput?.click();
  }

  public archiveCurrentThread(): void {
    console.log('archiveCurrentThread: Method called');
    const thread = this.selectedThread();
    console.log('archiveCurrentThread: Selected thread:', thread);
    if (!thread) {
      console.log('archiveCurrentThread: No thread selected');
      return;
    }

    const userId = this.session.user?.userId!;
    const customerId = this.session.user?.customer?._id!;

    console.log('archiveCurrentThread: Archiving thread', { conversationId: thread.conversationId, userId, customerId });

    this.messageService.archiveThreads([thread.conversationId], userId, customerId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          console.log('archiveCurrentThread: Archive successful');
          // Remove thread from the list immediately (if in inbox view)
          if (this.viewMode() === 'inbox') {
            const updatedThreads = this.threads().filter(t => t._id !== thread._id);
            this.threads.set(updatedThreads);
          } else {
            // If in archived view, just mark as archived
            const updatedThreads = this.threads().map(t =>
              t._id === thread._id ? { ...t, archived: true } : t
            );
            this.threads.set(updatedThreads);
          }

          // Clear selected thread since it's now archived
          this.selectedThread.set(null);
          this.messages.set([]);

          this.toaster.success('Conversation archived');
        },
        error: (error) => {
          console.error('archiveCurrentThread: Error archiving thread:', error);
          this.toaster.error('Failed to archive conversation');
        }
      });
  }

  public unarchiveCurrentThread(): void {
    console.log('unarchiveCurrentThread: Method called');
    const thread = this.selectedThread();
    console.log('unarchiveCurrentThread: Selected thread:', thread);
    if (!thread) {
      console.log('unarchiveCurrentThread: No thread selected');
      return;
    }

    const userId = this.session.user?.userId!;
    const customerId = this.session.user?.customer?._id!;

    console.log('unarchiveCurrentThread: Unarchiving thread', { conversationId: thread.conversationId, userId, customerId });

    this.messageService.unarchiveThreads([thread.conversationId], userId, customerId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          console.log('unarchiveCurrentThread: Unarchive successful');
          // Remove thread from the list immediately (if in archived view)
          if (this.viewMode() === 'archived') {
            const updatedThreads = this.threads().filter(t => t._id !== thread._id);
            this.threads.set(updatedThreads);
          } else {
            // If in inbox view, just mark as unarchived
            const updatedThreads = this.threads().map(t =>
              t._id === thread._id ? { ...t, archived: false } : t
            );
            this.threads.set(updatedThreads);
          }

          // Clear selected thread since it's now unarchived
          this.selectedThread.set(null);
          this.messages.set([]);

          this.toaster.success('Conversation restored to inbox');
        },
        error: (error) => {
          console.error('unarchiveCurrentThread: Error unarchiving thread:', error);
          this.toaster.error('Failed to restore conversation');
        }
      });
  }

  public deleteCurrentThread(): void {
    console.log('deleteCurrentThread: Method called');
    const thread = this.selectedThread();
    console.log('deleteCurrentThread: Selected thread:', thread);
    if (!thread) {
      console.log('deleteCurrentThread: No thread selected');
      return;
    }

    const userId = this.session.user?.userId!;
    const customerId = this.session.user?.customer?._id!;

    this.messageService.deleteThreads([thread.conversationId], userId, customerId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          // Remove thread from the list immediately
          const updatedThreads = this.threads().filter(t => t._id !== thread._id);
          this.threads.set(updatedThreads);

          // Clear selected thread since it's now deleted
          this.selectedThread.set(null);
          this.messages.set([]);

          this.toaster.success('Conversation deleted');
        },
        error: (error) => {
          console.error('Error deleting thread:', error);
          this.toaster.error('Failed to delete conversation');
        }
      });
  }

  public restoreCurrentThread(): void {
    console.log('restoreCurrentThread: Method called');
    const thread = this.selectedThread();
    console.log('restoreCurrentThread: Selected thread:', thread);
    if (!thread) {
      console.log('restoreCurrentThread: No thread selected');
      return;
    }

    const userId = this.session.user?.userId!;
    const customerId = this.session.user?.customer?._id!;

    console.log('restoreCurrentThread: Restoring thread', { conversationId: thread.conversationId, userId, customerId });

    // Call unarchive to restore from deleted
    this.messageService.unarchiveThreads([thread.conversationId], userId, customerId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          console.log('restoreCurrentThread: Restore successful');
          // Remove thread from the deleted list
          const updatedThreads = this.threads().filter(t => t._id !== thread._id);
          this.threads.set(updatedThreads);

          // Clear selected thread
          this.selectedThread.set(null);
          this.messages.set([]);

          this.toaster.success('Conversation restored to inbox');
        },
        error: (error) => {
          console.error('restoreCurrentThread: Error restoring thread:', error);
          this.toaster.error('Failed to restore conversation');
        }
      });
  }

  public permanentlyDeleteCurrentThread(): void {
    console.log('permanentlyDeleteCurrentThread: Method called');
    const thread = this.selectedThread();
    if (!thread) {
      console.log('permanentlyDeleteCurrentThread: No thread selected');
      return;
    }

    // Remove thread from the list immediately
    const updatedThreads = this.threads().filter(t => t._id !== thread._id);
    this.threads.set(updatedThreads);

    // Clear selected thread
    this.selectedThread.set(null);
    this.messages.set([]);

    this.toaster.success('Conversation permanently deleted');
  }

  public formatPhoneNumber(event: any): void {
    const input = event.target.value;
    console.log('formatPhoneNumber called with input:', input);

    // Remove all non-digit characters
    const digits = input.replace(/\D/g, '');
    console.log('Extracted digits:', digits);

    // Store raw digits
    this.rawPhoneNumber = digits;

    // Format based on length
    let formatted = '';
    if (digits.length === 0) {
      formatted = '';
    } else if (digits.length <= 3) {
      formatted = `(${digits}`;
    } else if (digits.length <= 6) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length <= 10) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else {
      // Limit to 10 digits
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      this.rawPhoneNumber = digits.slice(0, 10);
    }

    console.log('Formatted result:', formatted);
    this.composeRecipient = formatted;

    // Update the input value directly to ensure it reflects
    event.target.value = formatted;
  }

  // Contact edit methods
  public startEditingContact(): void {
    console.log('startEditingContact called');
    const thread = this.selectedThread();
    console.log('Selected thread:', thread);
    if (!thread) return;

    this.isEditingContact.set(true);
    console.log('isEditingContact set to true');
    // Initialize with phone number or existing contact name
    this.editContactName.set(this.getContactName(thread));
    this.editContactAvatar.set(this.getAvatarUrl(thread));
  }

  public cancelEditingContact(): void {
    this.isEditingContact.set(false);
    this.showAvatarPicker.set(false);
  }

  public saveContactChanges(): void {
    const thread = this.selectedThread();
    if (!thread) return;

    // Get the actual phone number (not the custom name)
    const phoneNumber = thread.from !== this.session.user?.selectedNumber?.number ? thread.from : thread.to;

    // Save custom name if changed
    const newName = this.editContactName().trim();
    if (newName && newName !== phoneNumber) {
      this.contactNamesCache.set(phoneNumber, newName);
    } else {
      // If name is cleared or set to phone number, remove custom name
      this.contactNamesCache.delete(phoneNumber);
    }

    // Update avatar cache if changed
    if (this.editContactAvatar() !== this.getAvatarUrl(thread)) {
      this.avatarCache.set(phoneNumber, this.editContactAvatar());
    }

    // Save to localStorage
    this.saveContactDataToStorage();

    this.toaster.success('Contact saved successfully');
    this.isEditingContact.set(false);
    this.showAvatarPicker.set(false);
  }

  public toggleAvatarPicker(): void {
    this.showAvatarPicker.set(!this.showAvatarPicker());
  }

  public selectAvatar(url: string): void {
    this.editContactAvatar.set(url);
    this.showAvatarPicker.set(false);
  }

  public getAvatarOptions(): string[] {
    // Offer colour variants of the contact's initials-based avatar.
    const label = this.editContactName().trim() || 'Contact';
    return Array.from({ length: 20 }, (_, i) => generateAvatar(`${label}-${i}`, label));
  }

  // Media helper methods
  public getFileName(url: string): string {
    const parts = url.split('/');
    return parts[parts.length - 1];
  }

  public openMedia(url: string): void {
    window.open(url, '_blank');
  }

  public onComposeKeydown(event: KeyboardEvent): void {
    // If Enter is pressed without Shift, send the message
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevent new line
      this.sendNewMessage();
    }
    // If Shift+Enter, allow default behavior (new line)
  }
}
